import { differenceInYears, parse } from "date-fns";

import type { ImperialBmiInputs, MetricBmiInputs } from "@/shared/models";
import type { DateOfBirthValues } from "@/shared/models/legacy-types";
import { formatDOB } from "./dates";

const INCHES_PER_FOOT = 12;
const CM_PER_INCH = 2.54;
const METERS_PER_INCH = 0.0254;
const KG_PER_POUND = 0.45359237;

const calculateHeightInMeters = (feet: number, inches: number) => {
  return (feet * INCHES_PER_FOOT + inches) * METERS_PER_INCH;
};

const getFeetAndInchesFromCm = (heightCm: number) => {
  const totalInches = heightCm / CM_PER_INCH;
  const feet = Math.floor(totalInches / INCHES_PER_FOOT);
  const inches = Math.round(totalInches % INCHES_PER_FOOT);
  return { feet, inches };
};

export const convertKgToPounds = (weightKg: number) => {
  return Math.round(weightKg / KG_PER_POUND);
};

export const convertPoundsToKg = (weightPounds: number) => {
  return Math.round(weightPounds * KG_PER_POUND);
};

export const convertMetricToImperialBmiInputs = ({
  heightInCentimeters = 0,
  weightInKilograms = 0,
}: Partial<MetricBmiInputs>) => {
  const { feet, inches } = getFeetAndInchesFromCm(heightInCentimeters);

  return {
    heightFeet: feet,
    heightInches: inches,
    weightInPounds: convertKgToPounds(weightInKilograms),
  };
};

export const convertImperialToMetricBmiInputs = ({
  heightFeet,
  heightInches,
  weightInPounds,
}: ImperialBmiInputs) => {
  const totalInches = heightFeet * INCHES_PER_FOOT + heightInches;
  const heightInCentimeters = totalInches * CM_PER_INCH;
  const weightInKilograms = weightInPounds * KG_PER_POUND;

  return {
    heightInCentimeters: Math.round(heightInCentimeters),
    weightInKilograms: Math.round(weightInKilograms),
  };
};

export const calculateBmi = ({
  heightFeet = 0,
  heightInches = 0,
  weightInPounds = 0,
}: Partial<ImperialBmiInputs>) => {
  const heightMeters = calculateHeightInMeters(heightFeet, heightInches);
  const weightKg = weightInPounds * KG_PER_POUND;
  const bmi = heightMeters && weightKg / Math.pow(heightMeters, 2);

  return isNaN(bmi) ? 0 : parseFloat(bmi.toFixed(1));
};

export const calculateAgeByDOB = (dob: DateOfBirthValues) => {
  const dobIso = formatDOB(dob);
  const dobDate = parse(dobIso, "yyyy-MM-dd", new Date());
  const age = differenceInYears(new Date(), dobDate);
  return age;
};
